import React, { useState, useEffect, useMemo } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  AiOutlineUserAdd,
  AiOutlineSearch,
  AiOutlineExclamationCircle
} from "react-icons/ai";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";

import CustomerService from "../../services/CustomerService"; // API Service
import AddCustomerModal from "../../modals/AddCustomer/AddCustomerModal";
import EditCustomerModal from "../../modals/EditCustomer/EditCustomerModal";
import CustomerDetailsModal from "../../modals/CustomerDetailsModal/CustomerDetailsModal";
import PrintableTable from "../../components/PrintableTable/PrintableTable";
import Loader from "../../components/Loader/Loader";

import "./CustomersList.css";
import "../../styles/action-icons.css";

const CustomersList = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(0);

  const [selectedCustomer, setSelectedCustomer] = useState({});
  const [showDetails, setShowDetails] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });

  // Load customers
  const loadCustomers = async () => {
    setLoading(true);
    try {
      const res = await CustomerService.getAll();
      // API returns either { customers: [...] } or [...direct array]
      const list = res.data.customers || res.data || [];
      setCustomers(list);
      setFilteredCustomers(list);
    } catch (err) {
      console.error("Failed to fetch customers:", err);
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  // Filter logic
  useEffect(() => {
    const lower = searchTerm.toLowerCase();
    setFilteredCustomers(
      customers.filter(c =>
        String(c.customer_id || c.ID).includes(lower) ||
        (c.firstName || c["First Name"] || "")
          .toLowerCase()
          .includes(lower) ||
        (c.lastName || c["Last Name"] || "")
          .toLowerCase()
          .includes(lower) ||
        (c.email || "").toLowerCase().includes(lower) ||
        (c.phone || "").toLowerCase().includes(lower) ||
        (c.address || "").toLowerCase().includes(lower)
      )
    );
  }, [searchTerm, customers]);

  // Handlers
  const rowClickHandler = customer => {
    setSelectedCustomer(customer);
    setShowDetails(true);
  };

  const editClickHandler = (e, customer) => {
    e.stopPropagation();
    setSelectedCustomer(customer || {});
    setShowEditModal(true);
  };

  const deleteClickHandler = (e, id) => {
    e.stopPropagation();
    setSelectedCustomerId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await CustomerService.remove(selectedCustomerId);
      toast.success("Customer deleted successfully", { autoClose: 2000 });
      await loadCustomers();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete customer");
    } finally {
      setShowDeleteModal(false);
      setSelectedCustomerId(0);
    }
  };

  // Checkbox handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedCustomerIds(filteredCustomers.map(c => c.customer_id ?? c.ID));
    } else {
      setSelectedCustomerIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedCustomerIds(prev =>
      prev.includes(id) ? prev.filter(cid => cid !== id) : [...prev, id]
    );
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedCustomerIds.length === 0) return;
    if (!window.confirm("Are you sure you want to delete the selected customers?")) return;
    try {
      await Promise.all(selectedCustomerIds.map(id => CustomerService.remove(id)));
      toast.success("Selected customers deleted successfully", { autoClose: 2000 });
      setSelectedCustomerIds([]);
      await loadCustomers();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete selected customers");
    }
  };

  const getComparableValue = (customer, key) => {
    const id = customer.customer_id ?? customer.ID;
    switch (key) {
      case 'id':
        return Number(id) || 0;
      case 'firstName':
        return (customer.firstName ?? customer["First Name"] ?? "").toLowerCase();
      case 'lastName':
        return (customer.lastName ?? customer["Last Name"] ?? "").toLowerCase();
      case 'email':
        return (customer.email ?? customer.Email ?? "").toLowerCase();
      case 'phone':
        return (customer.phone ?? customer.Phone ?? "").toLowerCase();
      case 'address':
        return (customer.address ?? customer.Address ?? "").toLowerCase();
      default:
        return '';
    }
  };

  const sortedCustomers = useMemo(() => {
    const data = [...filteredCustomers];
    if (!sortConfig?.key) return data;
    data.sort((a, b) => {
      const aVal = getComparableValue(a, sortConfig.key);
      const bVal = getComparableValue(b, sortConfig.key);
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [filteredCustomers, sortConfig]);

  const customersToDisplay = selectedCustomerIds.length > 0
    ? sortedCustomers.filter(c => selectedCustomerIds.includes(c.customer_id ?? c.ID))
    : sortedCustomers;

  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="sort-icon" aria-hidden="true" />;
    return sortConfig.direction === 'asc'
      ? <FaSortUp className="sort-icon" aria-hidden="true" />
      : <FaSortDown className="sort-icon" aria-hidden="true" />;
  };

  if (loading) {
    return <Loader message="Loading customers..." />;
  }

  return (
    <>
      <div className=" customers-list">
        <div className="customers-title">Customers</div>

        <div className="customers-header row">
          <div className="col add-btn-col">
            <button
              className="btn add-customer-button"
              onClick={() => setShowAddModal(true)}
            >
              <AiOutlineUserAdd /> Add Customer
            </button>
          </div>
          <div className="col search-bar-col">
            <div className="search-bar-wrapper">
              <span className="search-icon">
                <AiOutlineSearch />
              </span>
              <input
                type="text"
                placeholder="Search Customers..."
                className="search-bar"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Bulk action buttons */}
        {selectedCustomerIds.length > 0 && (
          <div className="bulk-actions">
            <button className="btn btn-danger" onClick={handleBulkDelete}>
              <FiTrash2 /> Delete Selected
            </button>
            {/* Export/Save buttons are handled by PrintableTable, which will use only selected customers */}
          </div>
        )}

        <div className="table-responsive">
          <PrintableTable 
            fileName="customers.pdf"
            title="Customers List"
            showOptions={true}
            data={customersToDisplay}
          >
            <table className="table customers-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedCustomerIds.length === filteredCustomers.length && filteredCustomers.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>
                    <button type="button" className="sortable-header" onClick={() => handleSort('id')}>
                      ID {renderSortIcon('id')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="sortable-header" onClick={() => handleSort('firstName')}>
                      First Name {renderSortIcon('firstName')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="sortable-header" onClick={() => handleSort('lastName')}>
                      Last Name {renderSortIcon('lastName')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="sortable-header" onClick={() => handleSort('email')}>
                      Email {renderSortIcon('email')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="sortable-header" onClick={() => handleSort('phone')}>
                      Phone {renderSortIcon('phone')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="sortable-header" onClick={() => handleSort('address')}>
                      Address {renderSortIcon('address')}
                    </button>
                  </th>
                  <th className="no-print">Action</th>
                </tr>
              </thead>
              <tbody>
                {customersToDisplay.length > 0 ? (
                  customersToDisplay.map(customer => {
                    const id = customer.customer_id ?? customer.ID;
                    const first = customer.firstName ?? customer["First Name"] ?? "";
                    const last = customer.lastName ?? customer["Last Name"] ?? "";
                    const email = customer.email ?? customer.Email ?? "";
                    const phone = customer.phone ?? customer.Phone ?? "";
                    const address = customer.address ?? customer.Address ?? "";
                    return (
                      <tr
                        key={id}
                        onClick={() => rowClickHandler(customer)}
                        style={{ cursor: "pointer" }}
                      >
                        <td className="text-center" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedCustomerIds.includes(id)}
                            onChange={() => handleSelectOne(id)}
                            className="no-click"
                          />
                        </td>
                        <td className="text-center">{id}</td>
                        <td className="text-center">{first}</td>
                        <td className="text-center">{last}</td>
                        <td className="text-center">{email}</td>
                        <td className="text-center">{phone}</td>
                        <td className="text-center">{address}</td>
                        <td className="text-center no-print no-click">
                          <div className="table-action-group">
                            <button
                              type="button"
                              className="table-icon-button edit-button no-click"
                              onClick={e => editClickHandler(e, customer)}
                              title="Edit customer"
                            >
                              <FiEdit2 />
                            </button>
                            <button
                              type="button"
                              className="table-icon-button delete-button no-click"
                              onClick={e => deleteClickHandler(e, id)}
                              title="Delete customer"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="8">No customers found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </PrintableTable>

          <CustomerDetailsModal
            show={showDetails}
            onHide={() => setShowDetails(false)}
            customer={selectedCustomer}
          />
        </div>

        <ToastContainer />

        <AddCustomerModal
          showModal={showAddModal}
          handleClose={() => setShowAddModal(false)}
          onCustomerAdded={loadCustomers}
        />

        <EditCustomerModal
          showModal={showEditModal}
          handleClose={() => {
            setShowEditModal(false);
            setSelectedCustomer({});
          }}
          customerData={selectedCustomer || {}}
          onCustomerUpdated={loadCustomers}
        />

        <Modal
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          className="delete-confirmation-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title className="title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-danger)' }}>
              <AiOutlineExclamationCircle style={{ fontSize: '1.6em', color: 'var(--color-danger)' }} />
              Confirm Deletion
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="confirmation-text" style={{ fontSize: 'var(--font-size-md)', color: 'var(--color-text)', margin: '1rem 0', textAlign: 'center' }}>
              Are you sure you want to delete this customer? This action cannot be undone.
            </div>
          </Modal.Body>
          <Modal.Footer style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
            <Button
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
              className="w-100"
              style={{ maxWidth: 120, background: 'var(--color-muted)', color: 'var(--color-text-light)', border: 'none' }}
            >
              No
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirmDelete}
              className="w-100"
              style={{ maxWidth: 120, background: 'var(--color-danger)', color: 'var(--color-text-light)', border: 'none' }}
            >
              Yes
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default CustomersList;


